import React, { useCallback } from 'react';
import {
    TextField,
    Button,
    InputAdornment,
    Stack,
    Box,
    Typography,
    Chip,
    FormControl,
    Select,
    MenuItem,
    Popover,
    Slider,
    Divider,
    Grid,
    Menu
} from '@mui/material';
import {
    Search as SearchIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Clear as ClearIcon,
    FilterList as FilterListIcon,
    WbTwilight as LifeRhythmIcon,
    CleaningServices as CleanlinessIcon,
    Chat as CommunicationIcon,
    HomeWork as StayingHomeIcon,
    People as VisitorsIcon,
    SmokingRooms as SmokingRoomsIcon,
    Pets as PetsIcon
} from '@mui/icons-material';
import debounce from 'lodash/debounce';

// 滞在期間
const stayDurations = [
    { value: 'short', label: '短期（1-3ヶ月）' },
    { value: 'medium', label: '中期（3-6ヶ月）' },
    { value: 'long', label: '長期（6ヶ月以上）' }
];

export const RENTER_RENT_RANGE = {
    min: 0,
    max: 200000,
    step: 10000
};

const rentMarks = [
    { value: 0, label: '¥0' },
    { value: 50000, label: '¥50,000' },
    { value: 100000, label: '¥100,000' },
    { value: 150000, label: '¥150,000' },
    { value: 200000, label: '¥200,000' }
];

export const AGE_RANGE = {
    min: 18,
    max: 60,
    step: 1
};

const ageMarks = [
    { value: 18, label: '18歳' },
    { value: 30, label: '30歳' },
    { value: 40, label: '40歳' },
    { value: 50, label: '50歳' },
    { value: 60, label: '60歳' }
];

const moveInPreferences = [
    { value: 'within_1_month', label: '1か月以内' },
    { value: 'within_3_months', label: '3か月以内' },
    { value: 'within_6_months', label: '6か月以内' },
    { value: 'more_than_6_months', label: '6か月以上' }
];

const genderOptions = [
    { value: 'male', label: '男性' },
    { value: 'female', label: '女性' }
];

const sortOptions = [
    { value: 'date_desc', label: '新着順' },
    { value: 'age_asc', label: '年齢が若い順' },
    { value: 'age_desc', label: '年齢が高い順' },
    { value: 'price_asc', label: '家賃が安い順' },
    { value: 'price_desc', label: '家賃が高い順' }
];

const stayDurationOptions = [
    { value: 'short', label: '短期（1-3ヶ月）' },
    { value: 'medium', label: '中期（3-6ヶ月）' },
    { value: 'long', label: '長期（6ヶ月以上）' }
];

const DEBOUNCE_DELAY = 800;

// ライフスタイル条件オプション定義
const matchingPreferencesOptions = {
    smoking: [
        { value: 'non-smoker', label: '喫煙しない', icon: SmokingRoomsIcon },
        { value: 'smoker', label: '喫煙する', icon: SmokingRoomsIcon }
    ],
    pets: [
        { value: 'no', label: 'ペットを飼っていない', icon: PetsIcon },
        { value: 'yes', label: 'ペットを飼っている', icon: PetsIcon }
    ],
    lifeRhythm: [
        { value: 'morning', label: '朝型', icon: LifeRhythmIcon },
        { value: 'night', label: '夜型', icon: LifeRhythmIcon }
    ],
    cleanliness: [
        { value: 'clean', label: 'きれい好き', icon: CleanlinessIcon },
        { value: 'relaxed', label: 'ある程度の散らかりはOK', icon: CleanlinessIcon }
    ],
    communication: [
        { value: 'active', label: '積極的に交流', icon: CommunicationIcon },
        { value: 'minimal', label: '必要最低限の交流', icon: CommunicationIcon }
    ],
    stayingHome: [
        { value: 'often', label: '在宅が多い', icon: StayingHomeIcon },
        { value: 'rarely', label: '外出が多い', icon: StayingHomeIcon }
    ],
    visitors: [
        { value: 'welcome', label: '友人を招くことがある', icon: VisitorsIcon },
        { value: 'rarely', label: 'ほとんど招かない', icon: VisitorsIcon }
    ]
};

function RenterSearchFilter({ filters, setFilters, onClear, totalUsers }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [priceAnchorEl, setPriceAnchorEl] = React.useState(null);
    const [moveInAnchorEl, setMoveInAnchorEl] = React.useState(null);
    const [genderAnchorEl, setGenderAnchorEl] = React.useState(null);
    const [ageAnchorEl, setAgeAnchorEl] = React.useState(null);
    const [stayDurationAnchorEl, setStayDurationAnchorEl] = React.useState(null);
    const [localArea, setLocalArea] = React.useState(filters.area);
    const [localPriceRange, setLocalPriceRange] = React.useState(filters.priceRange);
    const [localAgeRange, setLocalAgeRange] = React.useState(filters.ageRange);
    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);

    // デバウンスされた検索処理
    const debouncedSearch = useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, area: value }));
        }, 500),
        [setFilters]
    );

    const debouncedSetFilters = useCallback(
        debounce((newValue, filterType) => {
            setFilters(prev => ({
                ...prev,
                [filterType]: newValue
            }));
        }, 500),
        [setFilters]
    );

    // 検索テキスト変更時の処理
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setLocalArea(value);
        debouncedSearch(value);
    };

    // Enter キー押下時の処理
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            debouncedSearch.cancel();
            setFilters(prev => ({ ...prev, area: localArea }));
            e.target.blur();
        }
    };

    const handleSortClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setAnchorEl(null);
    };

    const handleSortChange = (value) => {
        setFilters(prev => ({ ...prev, sortBy: value }));
        handleSortClose();
    };

    const handleMoveInClick = (event) => {
        setMoveInAnchorEl(event.currentTarget);
    };

    const handleMoveInClose = () => {
        setMoveInAnchorEl(null);
    };

    const handleMoveInChange = (value) => {
        setFilters(prev => ({ ...prev, moveInPreference: value }));
        handleMoveInClose();
    };

    const handleOccupationChange = (e) => {
        setFilters(prev => ({ ...prev, occupation: e.target.value }));
    };

    const handleGenderClick = (event) => {
        setGenderAnchorEl(event.currentTarget);
    };

    const handleGenderClose = () => {
        setGenderAnchorEl(null);
    };

    const handleGenderChange = (value) => {
        setFilters(prev => ({ ...prev, gender: value }));
        handleGenderClose();
    };

    const handleAgeChange = (event, newValue) => {
        setLocalAgeRange(newValue);
        debouncedSetFilters(newValue, 'ageRange');
    };

    const formatAge = (value) => `${value}歳`;

    const handleStayDurationClick = (event) => {
        setStayDurationAnchorEl(event.currentTarget);
    };

    const handleStayDurationClose = () => {
        setStayDurationAnchorEl(null);
    };

    const handleStayDurationChange = (value) => {
        setFilters(prev => ({ ...prev, stayDuration: value }));
        handleStayDurationClose();
    };

    const handlePriceChange = (event, newValue) => {
        setLocalPriceRange(newValue);
        debouncedSetFilters(newValue, 'priceRange');
    };

    const formatRent = (value) => `¥${value.toLocaleString()}`;

    // アクティブな詳細フィルター数をカウント
    const countActiveDetailFilters = () => {
        let count = 0;
        if (filters.stayDuration) count++;
        if (filters.moveInPreference) count++;

        // 古いlifestyle構造の代わりに新しいmatchingPreferencesを使用
        if (filters.matchingPreferences?.smoking) count++;
        if (filters.matchingPreferences?.pets) count++;

        // 他のマッチング条件のカウント
        if (filters.matchingPreferences?.lifeRhythm) count++;
        if (filters.matchingPreferences?.cleanliness) count++;
        if (filters.matchingPreferences?.communication) count++;
        if (filters.matchingPreferences?.stayingHome) count++;
        if (filters.matchingPreferences?.visitors) count++;

        return count;
    };

    // アクティブなフィルターがあるかどうか
    const hasActiveFilters = () => {
        return filters.area ||
            filters.stayDuration ||
            filters.gender ||
            filters.matchingPreferences?.smoking ||
            filters.matchingPreferences?.pets ||
            filters.ageRange[0] > AGE_RANGE.min ||
            filters.ageRange[1] < AGE_RANGE.max ||
            filters.priceRange[0] > RENTER_RENT_RANGE.min ||
            filters.priceRange[1] < RENTER_RENT_RANGE.max ||
            filters.moveInPreference ||
            filters.matchingPreferences?.lifeRhythm ||
            filters.matchingPreferences?.cleanliness ||
            filters.matchingPreferences?.communication ||
            filters.matchingPreferences?.stayingHome ||
            filters.matchingPreferences?.visitors;
    };

    const handlePriceReset = () => {
        const resetValue = [RENTER_RENT_RANGE.min, RENTER_RENT_RANGE.max];
        setLocalPriceRange(resetValue);
        setFilters(prev => ({
            ...prev,
            priceRange: resetValue
        }));
        setPriceAnchorEl(null);
    };

    const handleAgeReset = () => {
        const resetValue = [AGE_RANGE.min, AGE_RANGE.max];
        setLocalAgeRange(resetValue);
        setFilters(prev => ({
            ...prev,
            ageRange: resetValue
        }));
        setAgeAnchorEl(null);
    };

    // フィルターチップのスタイル
    const filterChipStyle = {
        borderRadius: '20px',
        height: '36px',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        },
        '&.MuiChip-outlined': {
            borderColor: '#e0e0e0',
            color: 'text.secondary',
        },
        '&.MuiChip-filled': {
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
                backgroundColor: 'primary.dark',
            }
        },
        flex: { xs: '1 1 auto', sm: '0 1 auto' },
        minWidth: { xs: 'calc(50% - 8px)', sm: 'fit-content' }
    };

    // 検索バーのスタイル
    const searchBarStyle = {
        backgroundColor: '#fff',
        borderRadius: '40px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        p: 1,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        maxWidth: { xs: '100%', md: 600 },
        border: '1px solid #e0e0e0'
    };

    // マッチング条件の更新ハンドラー
    const handleMatchingPreferenceChange = (category, value) => {
        setFilters(prev => ({
            ...prev,
            matchingPreferences: {
                ...prev.matchingPreferences,
                [category]: prev.matchingPreferences?.[category] === value ? '' : value
            }
        }));
    };

    // 詳細フィルターを閉じる
    const handleDetailClose = () => {
        setFilterAnchorEl(null);
    };

    return (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* 検索バー */}
            <Box sx={{
                ...searchBarStyle,
                mx: 'auto'
            }}>
                <TextField
                    fullWidth
                    placeholder="市区町村・路線・駅等で検索できます"
                    value={localArea}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    variant="standard"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                    sx={{ px: 2 }}
                />
            </Box>

            {/* フィルターバー - 中央寄せレイアウト */}
            <Box sx={{
                mt: 2,
                width: '100%',
                maxWidth: { xs: '100%', md: 800 },
                mx: 'auto'
            }}>
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    spacing={0}
                    gap={1}
                    sx={{
                        pb: 1,
                        justifyContent: { xs: 'flex-start', md: 'center' }
                    }}
                >
                    {/* ユーザー数とフィルター */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%',
                            justifyContent: { xs: 'flex-start', md: 'center' },
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flex: { xs: '1 1 100%', sm: '0 0 auto' },
                            mb: { xs: 1, sm: 0 },
                            justifyContent: { xs: 'flex-start', md: 'center' }
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <span style={{ color: '#3498db', fontWeight: 600 }}>{totalUsers}</span>人のユーザー
                            </Typography>
                            {hasActiveFilters() && (
                                <Chip
                                    label="フィルタークリア"
                                    onDelete={onClear}
                                    deleteIcon={<ClearIcon />}
                                    sx={{
                                        backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                        color: '#3498db',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            backgroundColor: 'rgba(52, 152, 219, 0.12)'
                                        }
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            flex: '1 1 auto',
                            justifyContent: { xs: 'flex-start', md: 'center' }
                        }}>
                            <Button
                                variant="outlined"
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={handleSortClick}
                                sx={filterChipStyle}
                            >
                                並び替え
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setPriceAnchorEl(e.currentTarget)}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: localPriceRange[0] !== RENTER_RENT_RANGE.min || localPriceRange[1] !== RENTER_RENT_RANGE.max ? 'primary.main' : 'inherit',
                                    borderColor: localPriceRange[0] !== RENTER_RENT_RANGE.min || localPriceRange[1] !== RENTER_RENT_RANGE.max ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                家賃 {localPriceRange[0] !== RENTER_RENT_RANGE.min || localPriceRange[1] !== RENTER_RENT_RANGE.max ?
                                    `${formatRent(localPriceRange[0])} - ${formatRent(localPriceRange[1])}` :
                                    '指定なし'}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setAgeAnchorEl(e.currentTarget)}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: localAgeRange[0] !== AGE_RANGE.min || localAgeRange[1] !== AGE_RANGE.max ? 'primary.main' : 'inherit',
                                    borderColor: localAgeRange[0] !== AGE_RANGE.min || localAgeRange[1] !== AGE_RANGE.max ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                年齢 {localAgeRange[0] !== AGE_RANGE.min || localAgeRange[1] !== AGE_RANGE.max ?
                                    `${formatAge(localAgeRange[0])} - ${formatAge(localAgeRange[1])}` :
                                    '指定なし'}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={handleGenderClick}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: filters.gender ? 'primary.main' : 'inherit',
                                    borderColor: filters.gender ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                性別 {filters.gender ? (filters.gender === 'male' ? '男性' : '女性') : '指定なし'}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                startIcon={<FilterListIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: countActiveDetailFilters() > 0 ? 'primary.main' : 'inherit',
                                    borderColor: countActiveDetailFilters() > 0 ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                詳細条件
                                {countActiveDetailFilters() > 0 && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1,
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {countActiveDetailFilters()}
                                    </Box>
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {/* ソートメニュー */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleSortClose}
            >
                {sortOptions.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        selected={filters.sortBy === option.value}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* 詳細条件のポップオーバー */}
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={handleDetailClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 500 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 500 },
                        m: { xs: 2, sm: 0 },
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }
                }}
            >
                <Box sx={{ p: 3, width: 'auto', maxWidth: '100%' }}>
                    <Typography variant="h6" gutterBottom>詳細条件</Typography>

                    {/* 入居時期 */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>入居時期</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {moveInPreferences.map((option) => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    onClick={() => handleMoveInChange(option.value)}
                                    variant={filters.moveInPreference === option.value ? 'filled' : 'outlined'}
                                    color={filters.moveInPreference === option.value ? 'primary' : 'default'}
                                    sx={{
                                        borderRadius: '16px',
                                        '&:hover': {
                                            backgroundColor: filters.moveInPreference === option.value ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* 滞在期間 */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>滞在期間</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {stayDurationOptions.map((option) => (
                                <Chip
                                    key={option.value}
                                    label={option.label}
                                    onClick={() => handleStayDurationChange(option.value)}
                                    variant={filters.stayDuration === option.value ? 'filled' : 'outlined'}
                                    color={filters.stayDuration === option.value ? 'primary' : 'default'}
                                    sx={{
                                        borderRadius: '16px',
                                        '&:hover': {
                                            backgroundColor: filters.stayDuration === option.value ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* マッチング条件 */}
                    {Object.entries(matchingPreferencesOptions).map(([category, options]) => (
                        <Box key={category} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                {category === 'smoking' && '喫煙'}
                                {category === 'pets' && 'ペット'}
                                {category === 'lifeRhythm' && '生活リズム'}
                                {category === 'cleanliness' && '清潔感'}
                                {category === 'communication' && 'コミュニケーション'}
                                {category === 'stayingHome' && '在宅頻度'}
                                {category === 'visitors' && '来客'}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {options.map((option) => (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        onClick={() => handleMatchingPreferenceChange(category, option.value)}
                                        variant={filters.matchingPreferences?.[category] === option.value ? 'filled' : 'outlined'}
                                        color={filters.matchingPreferences?.[category] === option.value ? 'primary' : 'default'}
                                        sx={{
                                            borderRadius: '16px',
                                            '&:hover': {
                                                backgroundColor: filters.matchingPreferences?.[category] === option.value ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))}

                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                        <Button onClick={handleDetailClose}>
                            キャンセル
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDetailClose}
                        >
                            適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>

            {/* 家賃スライダーのポップオーバー */}
            <Popover
                open={Boolean(priceAnchorEl)}
                anchorEl={priceAnchorEl}
                onClose={() => setPriceAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 400 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
                        m: { xs: 2, sm: 0 }
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>家賃範囲</Typography>
                    <Box sx={{ px: 2, pt: 3 }}>
                        <Slider
                            value={localPriceRange}
                            onChange={handlePriceChange}
                            valueLabelDisplay="on"
                            valueLabelFormat={formatRent}
                            min={RENTER_RENT_RANGE.min}
                            max={RENTER_RENT_RANGE.max}
                            step={RENTER_RENT_RANGE.step}
                            marks={rentMarks}
                            sx={{
                                '& .MuiSlider-valueLabel': {
                                    backgroundColor: 'primary.main',
                                }
                            }}
                        />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Button onClick={handlePriceReset}>
                            クリア
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setPriceAnchorEl(null)}
                        >
                            適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>

            {/* 年齢スライダーのポップオーバー */}
            <Popover
                open={Boolean(ageAnchorEl)}
                anchorEl={ageAnchorEl}
                onClose={() => setAgeAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 400 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
                        m: { xs: 2, sm: 0 }
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>年齢範囲</Typography>
                    <Box sx={{ px: 2, pt: 3 }}>
                        <Slider
                            value={localAgeRange}
                            onChange={handleAgeChange}
                            valueLabelDisplay="on"
                            valueLabelFormat={formatAge}
                            min={AGE_RANGE.min}
                            max={AGE_RANGE.max}
                            step={AGE_RANGE.step}
                            marks={ageMarks}
                            sx={{
                                '& .MuiSlider-valueLabel': {
                                    backgroundColor: 'primary.main',
                                }
                            }}
                        />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Button onClick={handleAgeReset}>
                            クリア
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setAgeAnchorEl(null)}
                        >
                            適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>

            {/* 性別選択 */}
            <Menu
                anchorEl={genderAnchorEl}
                open={Boolean(genderAnchorEl)}
                onClose={handleGenderClose}
            >
                {genderOptions.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => handleGenderChange(option.value)}
                        selected={filters.gender === option.value}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}

export default RenterSearchFilter; 